import React from "react";

// @mui
import { GridColumnGroupingModel } from "@mui/x-data-grid";
import { styled, SxProps, Theme } from "@mui/material";

// mui components
import Box from "../mui/Box";
import DataGrid from "../mui/DataGrid";
import Pagination from "../mui/Pagination";

interface CustomGridColDef {
  sx?: SxProps<Theme>;
  cellClassName?: string;
}

interface TableProps {
  rows: { [key: string]: string | number }[] | any;
  columnGroupingModel?: GridColumnGroupingModel;
  columns: CustomGridColDef[] | any;
  secondRowOfHeaderSx?: SxProps<Theme>;
  firstRowOfHeaderSx?: SxProps<Theme>;
  thirdRowOfHeaderSx?: SxProps<Theme>;
  columnHeaderTitleSx?: SxProps<Theme>;
  rowCellsSx?: SxProps<Theme>;
  headerSx?: SxProps<Theme>;
  tableSx: SxProps<Theme>;
  count?: number | null;
  loading?: boolean;
  paginationSx?: SxProps<Theme>;
  height?: number | string;
  handlePageChange?: (event: React.ChangeEvent<unknown>, page: number) => void;
  sx?: any;
  currentPage?: number;
  getRowId?: any;
  pagination?: any;
  showColumnVerticalBorder?: boolean;
  showCellVerticalBorder?: boolean;
  disableColumnResize?: boolean;
}

const MagicTable: React.FC<TableProps> = (props) => {
  const {
    rows = [],
    columnGroupingModel = [],
    columns = [],
    secondRowOfHeaderSx = {},
    firstRowOfHeaderSx = {},
    thirdRowOfHeaderSx = {},
    columnHeaderTitleSx = {},
    paginationSx = {},
    rowCellsSx = {},
    headerSx = {},
    tableSx = { height: "auto", width: "100%" },
    count = 10,
    loading = false,
    handlePageChange,
    sx = {},
    currentPage = 1,
    getRowId,
    showCellVerticalBorder = true,
    showColumnVerticalBorder = true,
    disableColumnResize = false,
  } = props;

  const _Sx = {
    "& .MuiDataGrid-topContainer": {
      "& .css-yrdy0g-MuiDataGrid-columnHeaderRow": {
        '&[aria-rowindex="1"]': {
          backgroundColor: "#DADADA",
          color: "#242424",
          fontWeight: 600,
          fontSize: "14px",
          borderBottom: "none", // No border below the first header row
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          ...firstRowOfHeaderSx,
        },

        '&[aria-rowindex="2"]': {
          backgroundColor: "#FFFDF4",
          fontWeight: 600,
          fontSize: "12px",
          color: "red",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          borderBottom: "1px solid #ddd", // No border below the first header row

          ...secondRowOfHeaderSx,
        },

        '&[aria-rowindex="3"]': {
          backgroundColor: "#FFFDF4",
          fontWeight: 600,
          fontSize: "12px",
          color: "#676767",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          ...thirdRowOfHeaderSx,
        },

        "& .MuiDataGrid-columnHeader": {
          margin: 0,
          padding: 0,
          color: "#262626",
          fontWeight: 400,
          fontSize: "14px",
          minHeight: "100%",
          borderBottom: "none", // No border below header cells
          display: "flex",
          justifyContent: "center",
          textAlign: "center",

          alignItems: "center",
          ...headerSx,
        },
      },
    },

    "& .MuiDataGrid-cell": {
      textAlign: "center",
      padding: "0px",
      color: "black",
      display: "flex",
      backgroundColor: "#FFF",
      justifyContent: "center",
      alignItems: "center",
      ...rowCellsSx,
    },

    // Loader style customization
    "& .MuiDataGrid-overlay": {
      backgroundColor: "rgba(255, 255, 255, 0.7)",
    },
    "& .MuiDataGrid-loading": {
      color: "#FFCB08", // Custom loader color
    },
    "& .MuiCircularProgress-root": {
      color: "#FFCB08", // Custom color for the loader spinner
    },
    "& .MuiDataGrid-viewport": {
      overflow: "auto",
    },

    // "& .MuiDataGrid-columnHeader": {
    //   borderBottomWidth: "1px",
    //   // borderBottomStyle: "solid",
    //   boxSizing: "border-box",
    // },
    // "& .pinned-column": {
    //   position: "sticky",
    //   left: 0,
    //   backgroundColor: "red", // Background color for the pinned column
    //   zIndex: 10, // Ensure it stays above other columns
    //   borderRight: "1px solid #ddd", // Optional: Add a border
    //   width:"400px"
    // },

    // "& .pinned-column-1 ":{
    //   position: "sticky",
    //   backgroundColor: "red", // Background color for the pinned column
    //   zIndex: 10, // Ensure it stays above other columns
    //   width:"120px",

    //   left: 0,
    // },
    // "& .pinned-column-2": {
    //   position: "sticky",
    //   backgroundColor: "red", // Background color for the pinned column
    //   zIndex: 10, // Ensure it stays above other columns
    //   width:240,
    //   left: 120, /* Width of the first column */
    // },
    // "& .pinned-column-3": {
    //   position: "sticky",
    //   backgroundColor: "red", // Background color for the pinned column
    //   zIndex: 10,
    //   width:360,

    //   left: 360, /* Sum of widths of first and second columns */
    // },

    // '& .MuiDataGrid-columnHeader': {
    //   backgroundColor: '#f5f5f5', // Style column headers for consistency
    // },
    // Any additional custom styles passed through `sx`
    ...sx,
  };

  return (
    <Box>
      <Box sx={{ display: "flex", flexDirection: "column", mt: "10px" }}>
        <Box>
          <DataGrid
            rows={rows}
            columns={columns}
            columnGroupingModel={columnGroupingModel}
            checkboxSelection={false}
            disableColumnSorting={false}
            autoPageSize={false}
            hideFooter={true}
            hideFooterPagination={true}
            showColumnVerticalBorder={true}
            showCellVerticalBorder={showCellVerticalBorder}
            disableRowSelectionOnClick={showColumnVerticalBorder}
            loading={loading}
            disableColumnResize={disableColumnResize}
            getRowId={getRowId}
            density="standard"
            rowSpacingType="margin"
            columnHeaderHeight={50}
            disableColumnFilter={true}
            disableColumnMenu={true}
            // @ts-ignore
            style={{ ...tableSx }}
            // @ts-ignore
            sx={{ ..._Sx }}

            // sx={{
            //   "& .MuiDataGrid-columnHeader": {
            //     backgroundColor: "#DADADA",
            //   },
            //   "& .MuiDataGrid-columnHeaderTitle": {
            //     fontWeight: 550,
            //     fontSize: "14px",
            //     backgroundColor: "#DADADA",

            //   },
            //   "& .MuiDataGrid-row": {
            //     backgroundColor: "#FFFFFF",

            //   },
            //   "& .MuiDataGrid-row:hover": {
            //     backgroundColor: "#FFFFFF",
            //   },
            //   // "& .MuiDataGrid-cell": {
            //   //   display: "flex",
            //   //   alignItems: "center",
            //   // },
            //   "& .MuiDataGrid-loading": {
            //     color: "#FFCB08", // custom loader color (pink, but you can change this)
            //   },
            //   "& .MuiCircularProgress-root": {
            //     color: "#FFCB08", // custom color for the loader spinner
            //   },
            //   "& .MuiDataGrid-cell": {
            //     padding: "0px", // Ensure no padding is applied
            //     margin: "0px", // Reset any default margin
            //     display: "flex",
            //     justifyContent: "center", // Center horizontally
            //     alignItems: "center", // Center vertically
            //     textAlign: "center", // Center text
            //     backgroundColor: "#FFF", // Optional: Keep the background consistent
            //     ...rowCellsSx, // Allow additional styles to be passed via props
            //   },

            //   "& .MuiDataGrid-topContainer": {
            //     "& .css-yrdy0g-MuiDataGrid-columnHeaderRow": {
            //       '&[aria-rowindex="1"]': {
            //         backgroundColor: "red",
            //         color: "#242424",
            //         fontWeight: 600,
            //         fontSize: "14px",
            //         borderBottom: "1px solid #ddd", // No border below the first header row
            //         display: "flex",
            //         justifyContent: "center",
            //         alignItems: "center",
            //         textAlign: "center",
            //         ...firstRowOfHeaderSx,
            //       },
            //     }
            //   },

            //   ...sx,
            // }}
            // initialState={{ pinnedColumns: { left: ['region']} }}
          />
        </Box>

        {/* Pagination */}
        {count === null ? null : (
          <StyledBox sx={{ ...paginationSx }}>
            <Pagination
              sx={{ m: "auto" }}
              count={count}
              onChange={handlePageChange}
              page={currentPage}
            />
          </StyledBox>
        )}
      </Box>
    </Box>
  );
};

const StyledBox = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  // border: "1px solid rgb(204, 204, 204)",
  alignItems: "center",
  width: "100%",
  height: "50px",
  backgroundColor: "#FFF",
}));

export default MagicTable;
